"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getBorrowingBaseReportContainer = exports.createBorrowingBaseReportId = void 0;
/* eslint-disable no-param-reassign */
const uuid_1 = require("uuid");
const common_1 = require("@finley-technologies/common");
const report_1 = require("../cash/report");
const report_2 = require("../receivable/report");
// This file is intended to fully replace the existing borrowingBase.ts functionality
// When that file is deleted, this will be renamed
/**
 * ******* BORROWING BASE REPORT CALCULATION WORKFLOW *******
 * 1) Run the container using inputs (receivables require adding data, cash requires balances)
 * 2) [optional] If a reserve is relevant at the scope of the caller, update reserve numbers in the container after initial container run (report must be available prior to the reserve calculation)
 *      a) This step will only affect the cash component of data. Reserve is representative of cash that must be kept on hand to satisfy payment for short-term liabilities owed at next settlement. The receivable container will not change after reserve numbers are updated.
 * 3) Retrieve required data at any time - aggregations and recalculation will be triggered only prior to retrieving data (just in time, rather than after each loop of data updates for receivables)
 */
const createBorrowingBaseReportId = () => (0, uuid_1.v4)();
exports.createBorrowingBaseReportId = createBorrowingBaseReportId;
// Used to process all borrowing base components and generate a full report
const getBorrowingBaseReportContainer = ({ borrowingBaseReportId = (0, exports.createBorrowingBaseReportId)(), metadata, borrowingBaseMechanics, facilityMetrics, fundingConfiguration, borrowingBaseReportForLatestApprovedFundingRequestMetadata, }) => {
    const timestamp = new Date();
    const eligibilityTestConfigs = [];
    borrowingBaseMechanics.loanProducts.forEach((lp) => eligibilityTestConfigs.push(...lp.eligibilityTestConfigs));
    // Delegate all processing for receivable attributes + eligibility + concentration to receivable report container
    const receivableReportContainer = (0, report_2.getReceivableReportContainer)({
        borrowingBaseReportId,
        timestamp,
        eligibilityTestConfigs,
        concentrationLimitCalculationResults: facilityMetrics.concentrationLimitCalculationResults,
        multiLoanProductConcentrationLimits: borrowingBaseMechanics.multiLoanProductConcentrationLimits,
        borrowingBaseReportForLatestApprovedFundingRequestMetadata,
    });
    // Delegate all processing for cash attributes to cash container
    const cashReportContainer = (0, report_1.getCashReportContainer)({
        bankAccountCashBalances: facilityMetrics.bankAccountCashBalances,
    });
    return {
        addReceivable: (receivable) => receivableReportContainer.addReceivable(receivable),
        getBorrowingBaseReport: (params) => {
            const cashReserveParams = params
                ? {
                    borrowingBaseReserveAmount: params.borrowingBaseReserveAmount ?? null,
                    fundingConfiguration,
                }
                : undefined;
            const cashReport = cashReportContainer.getCashReport(cashReserveParams);
            const { assetValuation, effectiveReceivableAdvanceRate, effectiveReceivableValuationRate, basicReport: { id: _unused, ...receivableReport }, totalPerformingExposure, weightedAverageReceivableAdvanceRate, assignedEligibleExposureSinceLastApprovedFundingRequest, } = receivableReportContainer.getReceivableReport();
            // *** AGGREGATE CALCULATIONS (INSIGHTS) ***
            const totalBorrowingBaseAmount = receivableReport.borrowingBaseAmountAssets + cashReport.totalCash;
            const totalMaxAdvanceAmount = receivableReport.maxReceivableAdvanceAmount +
                cashReport.maxCashAdvanceAmount;
            const totalAssets = receivableReport.totalReceivableExposure +
                cashReport.totalEligibleBorrowingBaseAccountCash;
            const effectiveAdvanceRate = common_1.utils.divideByOrZero(totalBorrowingBaseAmount, totalAssets);
            const borrowingBaseUtilization = common_1.utils.divideByOrZero(facilityMetrics.currentPrincipalBalance, totalBorrowingBaseAmount);
            const totalFacilityUtilization = common_1.utils.divideByOrZero(facilityMetrics.currentPrincipalBalance, facilityMetrics.commitmentAmount);
            // *** AGGREGATE CALCULATIONS (BORROWING BASE AVAILABILITY) ***
            // The second component of the input for Math.min is represented differently from totalMaxAdvanceAmount
            // They may actually be the same thing, but it is unclear - TODO: @sachinr4 to review cash calculations more in depth
            const maximumAvailableAmount = Math.min(facilityMetrics.commitmentAmount, (receivableReport.maxReceivableAdvanceAmount ?? 0) +
                (cashReport.totalCashBorrowingBaseAmount ?? 0));
            const borrowingBaseAvailability = maximumAvailableAmount - facilityMetrics.currentPrincipalBalance;
            const borrowingBaseAvailabilityRate = common_1.utils.divideByOrZero(borrowingBaseAvailability, maximumAvailableAmount);
            // Consolidate properties from receivable report, cash report, and aggregate borrowing base calculations
            return {
                borrowingBaseReport: {
                    // METADATA
                    id: borrowingBaseReportId,
                    borrowerId: metadata.borrowerOrganizationId,
                    creditFacilityId: metadata.creditFacilityId,
                    creditFacilitySlug: metadata.creditFacilitySlug,
                    creditAgreementId: metadata.creditAgreementId,
                    creditAgreementVersion: metadata.creditAgreementVersion,
                    reportingDate: metadata.reportingDate,
                    fileVersion: metadata.fileVersion,
                    settlementDate: metadata.nextSettlementDate,
                    created_at: timestamp,
                    updated_at: timestamp,
                    // FUNDING CONFIGURATION
                    isBorrowingBaseReservePositiveExcessCollectionsOverReserve: false,
                    // FACILITY METRICS
                    commitmentAmount: facilityMetrics.commitmentAmount,
                    currentPrincipalBalance: facilityMetrics.currentPrincipalBalance,
                    // RECEIVABLE DATA
                    assetValuation,
                    effectiveReceivableValuationRate,
                    effectiveReceivableAdvanceRate,
                    weightedAverageReceivableAdvanceRate,
                    ...receivableReport,
                    // CASH DATA
                    ...cashReport,
                    // RESERVE DATA
                    borrowingBaseReserveAmount: params
                        ? params.borrowingBaseReserveAmount
                        : null,
                    cashReserveAmount: params ? params.cashReserveAmount : null,
                    // CLASS DETAILS DATA
                    classDetails: params.classDetails,
                    // AGGREGATE METRICS
                    totalBorrowingBaseAmount,
                    totalMaxAdvanceAmount,
                    totalAssets,
                    effectiveAdvanceRate,
                    borrowingBaseUtilization,
                    totalFacilityUtilization,
                    maximumAvailableAmount,
                    borrowingBaseAvailability,
                    borrowingBaseAvailabilityRate,
                    assignedEligibleExposureSinceLastApprovedFundingRequest,
                },
                eligibilityCriteriaCalculations: receivableReportContainer.getEligibilityCriteriaCalculations(),
                concentrationLimitCalculations: receivableReportContainer.getConcentrationLimitCalculations(),
                concentrationLimitMultiLoanProductCalculations: receivableReportContainer.getConcentrationLimitMultiLoanProductCalculations(),
                totalPerformingExposure, // Data lake use
            };
        },
        getLoanProductReceivableBorrowingBaseReport: (params) => {
            const loanProductReport = receivableReportContainer.getLoanProductReport(params);
            if (loanProductReport) {
                return {
                    ...loanProductReport,
                    borrowingBaseReportId,
                    loanProductId: params.loanProductId,
                    creditFacilityId: metadata.creditFacilityId,
                };
            }
            return null;
        },
        getLoanProductExternalReceivableIds: (params) => receivableReportContainer.getLoanProductExternalReceivableIds(params),
    };
};
exports.getBorrowingBaseReportContainer = getBorrowingBaseReportContainer;
//# sourceMappingURL=index.js.map